<template>
    <q-tab-panel name="EventResultsTab">
        <q-tabs v-model="selClass" align="left" dense no-caps narrow-indicator active-color="secondary"
                v-if="moreClasses">
            <q-tab :ripple="false" :name="boatClass.boatClass.id" :label="boatClass.eventClassName"
                   v-for="boatClass in attendedClasses"
                   :key="boatClass.id" />
        </q-tabs>

        <div class="text-right filter-bar" v-if="allowOnlyCaptainsOption">
            <q-toggle
                v-model="showOnlyCaptains" color="secondary"
                :label="showOnlyCaptains ? ts( 'showOnlyCaptains' ) : ts( 'showCrewOnBoats' )"
                @update:model-value="saveShowOnlyCaptains" />
        </div>

        <div class="q-pa-md" v-if="isCategoriesUsed">
            <q-select
                v-model="categoryDisplayed" :label="ts( 'categoryDisplayed' )" :options="categoryList"
                option-value="value" option-label="label" class="qe"
                emit-value map-options input-debounce="300" />
        </div>

        <q-markup-table flat class="q-mt-lg">
            <thead>
            <tr>
                <th class="text-right">{{ ts( 'orderShortcut' ) }}</th>
                <th class="text-left">{{ ts( 'sailNo' ) }}</th>
                <th class="text-left" v-if="useBoatNames && isBoatNamesUsed">{{ ts( 'boatName' ) }}</th>
                <th class="text-left">{{ ts( 'crew' ) }}</th>
                <th class="text-left" v-if="isCategoriesUsed && categoryDisplayed === null">{{ ts( 'category' ) }}</th>
                <th class="text-right" v-for="( race, index ) in availableRaces" :key="race.id">R{{ index + 1 }}</th>
                <th class="text-right">{{ ts( 'total' ) }}</th>
                <th class="text-right" v-if="categoryDisplayed === null">{{ ts( 'logPoints' ) }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-if="results.length === 0 || availableRaces.length === 0">
                <td colspan="6" class="text-center">
                    <div class="q-mt-md">
                        <q-icon name="warning" color="negative" size="40px" />
                        <span class="q-ml-md">{{ ts( 'noEnteredResults' ) }}</span>
                    </div>
                </td>
            </tr>
            <tr v-for="( boat ) in results" :key="boat.id" v-else>
                <td class="text-right">{{ boat.index }}.</td>
                <td>{{ boat.boat.sailNo }}</td>
                <td v-if="useBoatNames && isBoatNamesUsed">{{ boat.boat.name }}</td>
                <td class="minicrewlist">
                    <div v-if="showOnlyCaptains">
                        {{ boat.boat.orderedCrewList.length > 0 ? boat.boat.orderedCrewList[ 0 ].reverseFullName : '' }}
                    </div>
                    <div v-for="crew in boat.boat.orderedCrewList" :key="crew.id" :class="crew.captain ? 'captain' : ''"
                         v-else>
                        {{ crew.reverseFullName }}
                    </div>
                </td>
                <td class="minicrewlist" v-if="isCategoriesUsed && categoryDisplayed === null">
                    <div v-for="category in boat.boat.orderedCategories" :key="category.id">
                        {{ category.visibleShortcut }}
                    </div>
                </td>
                <td class="text-right" v-for="race in boat.results" :key="race.race"
                    :class="{ 'discarded': race.isDiscarded, 'auto_result': ! race.decided }"
                    :title="tcs( 'pointsNo', ns( race.points, 'points' ) )">
                    {{ race.result }}{{ race.isDiscarded ? '*' : '' }}
                </td>
                <td class="text-right text-weight-bold">{{ ns( boat.points, 'points' ) }}</td>
                <td class="text-right" v-if="categoryDisplayed === null">{{ ns( boat.logPoints, 'logPoints' ) }}</td>
            </tr>
            </tbody>
        </q-markup-table>

        <div class="text-right q-mt-lg" v-if="results.length > 0 && availableRaces.length > 0">
            <q-btn :label="ts( categoryDisplayed === null ? 'printSemiResults' : 'printCategorySemiResults' )" outline
                   color="secondary" @click="printResults( false, true )"
                   v-if="allowOnlyCaptainsOption && showOnlyCaptains" />
            <q-btn :label="ts( categoryDisplayed === null ? 'printSemiResults' : 'printCategorySemiResults' )" outline
                   color="secondary" @click="printResults( false )"
                   v-else-if="!allowOnlyCaptainsOption" />
            <q-btn-dropdown
                outline color="secondary" :label="ts( 'printSemiResults' )" v-else>
                <q-list>
                    <q-item clickable v-close-popup @click="printResults( false, false )">
                        <q-item-section>
                            {{ ts( 'withCrew' ) }}
                        </q-item-section>
                    </q-item>
                    <q-item clickable v-close-popup @click="printResults( false, true )">
                        <q-item-section>
                            {{ ts( 'onlyCaptains' ) }}
                        </q-item-section>
                    </q-item>
                </q-list>
            </q-btn-dropdown>

            <q-btn :label="ts( categoryDisplayed === null ? 'printResults' : 'printCategoryResults' )" outline
                   color="secondary" class="q-ml-md"
                   @click="printResults( true, true )" v-if="allowOnlyCaptainsOption && showOnlyCaptains" />
            <q-btn :label="ts( categoryDisplayed === null ? 'printResults' : 'printCategoryResults' )" outline
                   color="secondary" class="q-ml-md" @click="printResults( true )"
                   v-else-if="!allowOnlyCaptainsOption" />
            <q-btn-dropdown class="q-ml-md"
                            outline color="secondary" :label="ts( 'printResults' )" v-else>
                <q-list>
                    <q-item clickable v-close-popup @click="printResults( true, false )">
                        <q-item-section>
                            {{ ts( 'withCrew' ) }}
                        </q-item-section>
                    </q-item>
                    <q-item clickable v-close-popup @click="printResults( true, true )">
                        <q-item-section>
                            {{ ts( 'onlyCaptains' ) }}
                        </q-item-section>
                    </q-item>
                </q-list>
            </q-btn-dropdown>
        </div>
    </q-tab-panel>
</template>

<script setup lang="ts">
import { otherResults } from '@/orm/catalogs/OtherResults'
import { localization } from '@/plugins/i18n'
import { DateFormat, ds, ts, tcs, ns } from '@/plugins/i18n-formatted'
import nvl from '@/utils/nvl'
import { Options } from '@model/Setting'
import EventsRepo from '@repo/EventsRepo'
import SettingsRepo from '@repo/SettingsRepo'
import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import { useRepo } from 'pinia-orm'
import { QMarkupTable, QSelect, QTab, QTabPanel, QTabs } from 'quasar'
import { sprintf } from 'sprintf-js'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'

pdfMake.addVirtualFileSystem( pdfFonts )

const route = useRoute()

const event = computed( () => ( useRepo( EventsRepo ).getEvent( String( route.params.eventId ), true ) ) )

useRepo( EventsRepo ).selectEvent( event.value.id )

const attendedClasses = computed( () => ( event.value?.orderedClasses ) )
const moreClasses = computed( () => ( attendedClasses.value.length > 1 ) )
const selClass = ref( attendedClasses.value[ 0 ].boatClass.id )

const results = computed( () => ( event.value.results( selClass.value, categoryDisplayed.value ) ) )

const availableRaces = computed( () => ( event.value.availableRaces( selClass.value ) ) )

const useBoatNames = computed( () => ( event.value.getClass( selClass.value )?.boatClass.useBoatNames ) )

const maxCrewOnClass = computed( () => ( event.value.getClass( selClass.value )?.maxCrew ?? 0 ) )
const isBoatNamesUsed = computed( () => ( event.value.getClass( selClass.value )?.isBoatNamesUsed ?? false ) )
const allowOnlyCaptainsOption = computed( () => ( maxCrewOnClass.value >= 3 ) )
const showOnlyCaptains = ref( event.value.showOnlyCaptains )
const isCategoriesUsed = computed( () => ( results.value.some( result => result.boat.categories.length > 0 ) ) )

const categoryDisplayed = ref( null )
const categoryList = computed( () => ( [
    { value: null, label: ts( 'categoryFull-all' ) },
    ...event.value.getClass( selClass.value ).orderedCategories.map( category => ( {
        value: category.id,
        label: category.visibleTitle,
        filename: category.fileName
    } ) )
] ) )

function saveShowOnlyCaptains ( value ) {
    useRepo( EventsRepo ).save( { id: event.value.id, showOnlyCaptains: value } )
}

function printResults ( final = false, onlyCaptains = false ) {
    if ( event.value.forceEnglishReports )
        localization.global.locale.value = 'en'

    const eventClass = event.value.getClass( selClass.value )
    const useDesc = event.value.descInResultsTitle !== null && event.value.descInResultsTitle !== undefined && event.value.descInResultsTitle.length !== 0
    const initialColNum = ( ( eventClass.boatClass.useBoatNames || useDesc ) ? 2 : 4 )
    const landscape = availableRaces.value.length >= initialColNum
    const inLineResults = initialColNum + ( landscape ? 6 : 2 )

    const genHeader = availableRaces.value.map( ( race, index ) => ( {
        text: ( index + 1 ) + '.',
        style: 'em_race_result'
    } ) )

    const innerHeader = Array.range( 0, Math.ceil( genHeader.length / inLineResults ) ).map( i => genHeader.slice( i * inLineResults, ( i + 1 ) * inLineResults ) )

    if ( innerHeader.length > 1 )
        while ( innerHeader[ innerHeader.length - 1 ].length < inLineResults )
            innerHeader[ innerHeader.length - 1 ].push( '' )

    const now = ds( new Date(), DateFormat.DateTimeHuman )

    const otherDetails = results.value
        .map( ( boat ) => boat.results
            .filter( ( race ) => typeof race.result !== 'number' )
            .map( ( race ) => [ race.result, race.points, race.race, boat.boat.sailNo ] )
        )
        .reduce( ( accumulator, current ) => accumulator.concat( current ), [] )

    const organizedDetails = new Map(
        Map.groupBy( otherDetails, ( res ) => res[ 0 ] )
            .entries().toArray()
            .map( ( [ code, records ] ) => [ code, Map.groupBy( records, ( res ) => res[ 1 ] ) ] )
    )

    const legend = otherResults().filter( ( other ) => organizedDetails.has( other.value ) )
        .map( ( l ) => ( {
            value: l.value, short: l.short, details: organizedDetails.get( l.value ),
            points: organizedDetails.get( l.value ).size === 1 ? organizedDetails.get( l.value ).entries().toArray()[ 0 ][ 0 ] : null
        } ) )

    const legendColumns = landscape ? 4 : 3
    const legendLength = []

    // console.log( legend )

    let placed = 0

    for ( let leftColumns = legendColumns; leftColumns >= 1; leftColumns-- ) {
        legendLength.push( placed )

        placed += Math.ceil( ( legend.length - placed ) / leftColumns )
    }

    const content = {
        pageSize: 'A4',
        pageOrientation: landscape ? 'landscape' : 'portrait',
        pageMargins: [ 20, 40, 20, 20 ],
        header: ( currentPage ) => ( [
            {
                marginLeft: 20,
                marginRight: 20,
                marginTop: 15,
                table: {
                    headerRows: 0,
                    widths: [ '*', 'auto' ],
                    body: [
                        [
                            {
                                text: ts( final ? 'final-results' : 'intermediate-results', { timestamp: now } ),
                                style: 'h1'
                            },
                            { marginTop: 6, text: ts( 'pageNo', { pageNo: currentPage } ), alignment: 'right' }
                        ]
                    ]
                },
                layout: {
                    vLineWidth: () => ( 0 ),
                    hLineWidth: ( i, node ) => ( i === node.table.body.length ),
                    hLineColor: 'black'
                }
            }
        ] ),
        content: [
            {
                marginTop: 8,
                table: {
                    headerRows: 0,
                    widths: [ '*', 'auto' ],
                    body: [
                        [
                            {
                                text: [
                                    { text: ts( 'event' ) + ':  ', style: 'em' },
                                    { text: event.value.title?.shorten( 50 ), style: 'common' }
                                ]
                            },
                            {
                                text: [
                                    { text: ts( 'eventStart' ) + '  ', style: 'em' },
                                    { text: ds( new Date( event.value.from ), DateFormat.EventTo ), style: 'common' },
                                    { text: '  ' + ts( 'eventUntil' ) + '  ', style: 'em' },
                                    { text: ds( new Date( event.value.to ), DateFormat.EventTo ), style: 'common' }
                                ]
                            }
                        ],
                        [
                            {
                                text: [
                                    { text: ts( 'ctlBare' ) + ':  ', style: 'em' },
                                    { text: event.value.ctlId, style: 'common' }
                                ]
                            },
                            {
                                text: [
                                    { text: ts( 'mainReferee' ) + ':  ', style: 'em' },
                                    { text: event.value.mainReferee?.reverseFullNameWithId, style: 'common' }
                                ]
                            }
                        ],
                        [
                            {
                                text: [
                                    { text: ts( 'eventOrganizer' ) + ':  ', style: 'em' },
                                    { text: event.value.clubTitleShort?.shorten( 50 ), style: 'common' }
                                ]
                            },
                            {
                                text: [
                                    { text: ts( 'venue' ) + ':  ', style: 'em' },
                                    { text: event.value.venueTitle?.shorten( 50 ), style: 'common' }
                                ]
                            }
                        ],
                        [
                            {
                                text: [
                                    { text: ts( 'director' ) + ':  ', style: 'em' },
                                    { text: event.value.director?.shorten( 50 ), style: 'common' }
                                ]
                            },
                            {
                                text: [
                                    { text: ts( 'sponsor' ) + ':  ', style: 'em' },
                                    { text: event.value.sponsor?.shorten( 50 ), style: 'common' }
                                ]
                            }
                        ]
                    ]
                },
                layout: {
                    vLineWidth: () => ( 0 ),
                    hLineWidth: () => ( 0 )
                }
            },
            {
                marginTop: 8,
                marginBottom: 8,
                table: {
                    headerRows: 0,
                    widths: [ '*', 'auto', ...( categoryDisplayed.value === null ? [ 'auto', 'auto' ] : [] ) ],
                    body: [
                        [
                            {
                                marginTop: 4,
                                marginBottom: 4,
                                marginLeft: 4,
                                text: [
                                    { text: ts( 'boatClassShortcut' ) + ':  ', style: 'em' },
                                    {
                                        text: eventClass.eventClassName,
                                        style: 'em'
                                    }
                                ]
                            },
                            {
                                marginTop: 4,
                                marginBottom: 4,
                                marginLeft: 8,
                                marginRight: 8,
                                alignment: 'right',
                                text: [
                                    { text: ts( 'noOfBoats' ) + ':  ', style: 'em' },
                                    { text: results.value.length, style: 'em' }
                                ]
                            },
                            ...( categoryDisplayed.value === null ? [
                                {
                                    marginTop: 4,
                                    marginBottom: 4,
                                    marginLeft: 8,
                                    marginRight: 8,
                                    alignment: 'right',
                                    text: [
                                        { text: ts( 'plannedCoefficient' ) + ':  ', style: 'em' },
                                        { text: eventClass.weight, style: 'em' }
                                    ]
                                },
                                {
                                    marginTop: 4,
                                    marginBottom: 4,
                                    marginRight: 4,
                                    alignment: 'right',
                                    text: [
                                        { text: ts( 'coefficient' ) + ':  ', style: 'em' },
                                        { text: eventClass.calculatedWeight, style: 'em' }
                                    ]
                                }
                            ] : [] )
                        ],
                        ...( categoryDisplayed.value !== null ? [ [
                            {
                                colSpan: 2,
                                marginBottom: 4,
                                marginLeft: 4,
                                text: [
                                    { text: ts( 'category' ) + ':  ', style: 'em' },
                                    {
                                        text: categoryList.value.find( c => c.value === categoryDisplayed.value )?.label ?? '',
                                        style: 'em'
                                    }
                                ]
                            }
                        ]
                        ] : [] )
                    ]
                },
                layout: {
                    vLineWidth: ( i, node ) => ( ( i === 0 || i === node.table.widths.length ) ? 1 : 0 ),
                    hLineWidth: ( i, node ) => ( ( i === 0 || i === node.table.body.length ) ? 1 : 0 ),
                    hLineColor: 'black',
                    vLineColor: 'black'
                }
            },
            {
                table: {
                    headerRows: Math.ceil( availableRaces.value.length / inLineResults ),
                    widths: [ 'auto', '*', '*', ...( eventClass.boatClass.useBoatNames && isBoatNamesUsed.value ? [ '*' ] : [] ),
                        ...( useDesc ? [ '*' ] : [] ),
                        'auto', ...( categoryDisplayed.value === null ? [ 'auto' ] : [] ),
                        'auto', '*', ...innerHeader[ 0 ].map( () => landscape ? 26 : 24 ), 'auto',
                        ...( ( final && categoryDisplayed.value === null ) ? [ 'auto' ] : [] )
                    ],
                    body: [
                        ...innerHeader.map( ( inner, index ) => (
                            [
                                ...( index === innerHeader.length - 1 ? [
                                    { text: ts( 'orderShortcut' ).toLowerCase(), style: 'em' },
                                    { text: ts( 'sail' ).toLowerCase(), style: 'em' },
                                    ...( eventClass.boatClass.useBoatNames && isBoatNamesUsed.value ? [ {
                                        text: ts( 'boat' ).toLowerCase(),
                                        style: 'em'
                                    }
                                    ] : [] ),
                                    ...( useDesc ? [ {
                                        text: event.value.descInResultsTitle,
                                        style: 'em'
                                    } ] : [] ),
                                    { text: ts( 'crew' ).toLowerCase(), style: 'em' },
                                    { text: ts( 'regId' ).toLowerCase(), style: 'em' },
                                    ...( categoryDisplayed.value === null ? [ {
                                        text: ts( 'categoryShortcut' ).toLowerCase(),
                                        style: 'em'
                                    }
                                    ] : [] ),
                                    { text: ts( 'bornShortcut' ).toLowerCase(), style: 'em' },
                                    { text: ts( 'club' ).toLowerCaseFirst(), style: 'em' }
                                ] : [ '', '', ...( eventClass.boatClass.useBoatNames && isBoatNamesUsed.value ? [ '' ] : [] ), '', '', '', '' ] ),
                                ...inner,
                                ...( index === innerHeader.length - 1 ? [
                                    { text: ts( 'points' ).toLowerCase(), style: 'em', alignment: 'right' },
                                    ...( ( final && categoryDisplayed.value === null ) ? [ {
                                        text: ts( 'logPointsPrint' ).toLowerCase(),
                                        style: 'em',
                                        alignment: 'right'
                                    }
                                    ] : [] )
                                ] : [ '', ...( ( final && categoryDisplayed.value === null ) ? [ '' ] : [] ) ] )
                            ]
                        ) ),
                        ...( results.value.map(
                            function ( boat ) {
                                const res = boat.results.map( ( race ) => ( {
                                    text: race.result + ( race.isDiscarded ? '*' : '' ),
                                    style: race.decided ? '' : 'auto_result'
                                } ) )

                                let template = [
                                    { text: sprintf( '%d.', boat.index ), alignment: 'right' },
                                    boat.boat.sailNo,
                                    ...( eventClass.boatClass.useBoatNames && isBoatNamesUsed.value ? [ {
                                        text: nvl( boat.boat?.name, '' ).shorten( landscape ? 30 : 20 ),
                                        style: 'compact'
                                    }
                                    ] : [] ),
                                    ...( useDesc ? [ {
                                        text: nvl( boat.boat?.detail, '' ).shorten( landscape ? 30 : 20 ),
                                        style: 'compact'
                                    } ] : [] ),
                                    '',
                                    '',
                                    ...( categoryDisplayed.value === null
                                        ? [ {
                                            text: boat.boat.orderedCategories.map( category => category.visibleShortcut ).join( ', ' ),
                                            style: landscape ? '' : 'compact'
                                        }
                                        ] : [] ),
                                    '',
                                    '',
                                    ...res.slice( 0, innerHeader[ 0 ].length ),
                                    { text: ns( boat.points, 'points' ), alignment: 'right', style: 'em' },
                                    ...( ( final && categoryDisplayed.value === null ) ? [ {
                                        text: ns( boat.logPoints, 'logPoints' ),
                                        alignment: 'right',
                                        style: landscape ? '' : 'compact'
                                    }
                                    ] : [] )
                                ]

                                if ( boat.boat.crew === undefined || boat.boat.crew === null || boat.boat.crew.length === 0 ) {
                                    return innerHeader.map( ( ign, index ) => {
                                        let line = template

                                        const rowRes = res.slice( ( index + 1 ) * inLineResults, ( index + 2 ) * inLineResults )

                                        while ( rowRes.length < inLineResults )
                                            rowRes.push( '' )

                                        template = [ '', '', ...( eventClass.boatClass.useBoatNames && isBoatNamesUsed.value ? [ '' ] : [] ),
                                            ...( useDesc ? [ '' ] : [] ),
                                            '', '', ...( categoryDisplayed.value === null ? [ '' ] : [] ), '', '', ...rowRes, '',
                                            ...( ( final && categoryDisplayed.value === null ) ? [ '' ] : [] )
                                        ]

                                        return line
                                    } )
                                }

                                let limitedCrew = boat.boat.crew

                                if ( onlyCaptains )
                                    limitedCrew = limitedCrew.filter( c => c.captain )

                                let crewRes = limitedCrew.map( function ( crew, index ) {
                                    let line = template

                                    const rowRes = res.slice( ( index + 1 ) * inLineResults, ( index + 2 ) * inLineResults )

                                    while ( rowRes.length < innerHeader[ 0 ].length )
                                        rowRes.push( '' )

                                    template = [ '', '', ...( eventClass.boatClass.useBoatNames && isBoatNamesUsed.value ? [ '' ] : [] ),
                                        ...( useDesc ? [ '' ] : [] ),
                                        '', '', ...( categoryDisplayed.value === null ? [ '' ] : [] ), '', '', ...rowRes, '',
                                        ...( ( final && categoryDisplayed.value === null ) ? [ '' ] : [] )
                                    ]

                                    let baseOffset = 4

                                    if ( eventClass.boatClass.useBoatNames && isBoatNamesUsed.value )
                                        baseOffset++

                                    if ( useDesc )
                                        baseOffset++

                                    if ( categoryDisplayed.value === null )
                                        baseOffset++

                                    let otherBaseOffset = 2

                                    if ( eventClass.boatClass.useBoatNames && isBoatNamesUsed.value )
                                        otherBaseOffset++

                                    if ( useDesc )
                                        otherBaseOffset++

                                    line[ otherBaseOffset ] = crew.reverseFullName?.shorten( 25 ) ?? ''
                                    line[ otherBaseOffset + 1 ] = crew.regId

                                    line[ baseOffset ] = crew.birthYear ?? ''
                                    line[ baseOffset + 1 ] = {
                                        text: crew.visibleClub?.shorten( landscape ? 30 : 20 ) ?? '',
                                        style: landscape ? '' : 'compact'
                                    }

                                    return line
                                } )

                                const solved = boat.boat.crew.length

                                crewRes = [ ...crewRes, ...Array.range( solved + 1, innerHeader.length + 1 ).map( ( ign, index ) => {
                                    let line = template

                                    const rowRes = res.slice( ( index + solved + 1 ) * inLineResults, ( index + solved + 2 ) * inLineResults )

                                    while ( rowRes.length < innerHeader[ 0 ].length )
                                        rowRes.push( '' )

                                    template = [ '', '', ...( eventClass.boatClass.useBoatNames && isBoatNamesUsed.value ? [ '' ] : [] ),
                                        ...( useDesc ? [ '' ] : [] ),
                                        '', '', ...( categoryDisplayed.value === null ? [ '' ] : [] ), '', ...rowRes, '', '',
                                        ...( ( final && categoryDisplayed.value === null ) ? [ '' ] : [] )
                                    ]

                                    return line
                                } )
                                ]

                                return crewRes
                            }
                        ).flat( 1 ) )
                    ]
                },
                layout: {
                    vLineWidth: () => ( 0 ),
                    hLineWidth: ( i, node ) => ( ( i === node.table.headerRows || i === node.table.body.length ) ? 1 : 0 ),
                    vLineColor: 'black'
                },
                style: 'results'
            },
            {
                marginTop: 12,
                columnGap: 16,
                columns: [
                    ...Array.range( 0, legendColumns ).map( i => ( {
                        width: '*',
                        stack: legend.slice( legendLength[ i ], i === 3 ? legend.length + 1 : legendLength[ i + 1 ] )
                            .map( result => result.value + ' – ' + result.short + ( result.points !== null ? ' ('
                                + tcs( 'pointsNo', result.points, { named: { n: ns( result.points, 'points' ) } } ) + ')' : '' ) )
                    } ) )
                ],
                style: 'legend'
            },
            ...( ( eventClass.orderedCategories.length > 0 && categoryDisplayed.value === null ) ? [
                {
                    headerRows: 1,
                    marginTop: 16,
                    table: {
                        widths: [ 'auto', 'auto', '*' ],
                        body: [
                            [
                                { text: ts( 'shortcut' ), style: 'em' },
                                { text: ts( 'category' ), style: 'em' },
                                { text: ts( 'conditions' ), style: 'em' }
                            ],
                            ...eventClass.orderedCategories.map( category => ( [ category.visibleShortcut, category.visibleTitle, category.explanation ] ) )
                        ]
                    },
                    layout: {
                        vLineWidth: () => ( 0 ),
                        hLineWidth: () => ( 0 )
                    },
                    style: 'legend'
                }
            ] : [] ),
            {
                marginTop: 8,
                text: ts( final ? 'finalResultsInfo' : 'tempResultsInfo' ),
                style: 'legend'
            }
        ],
        defaultStyle: {
            font: 'Roboto',
            fontSize: 10,
            opacity: .6
        },
        styles: {
            h1: {
                fontSize: 16,
                bold: true,
                opacity: 1
            },
            common: {},
            em: {
                bold: true,
                opacity: 1
            },
            em_race_result: {
                bold: true,
                opacity: 1,
                fontSize: 8
            },
            results: {
                fontSize: 9
            },
            compact: {
                fontSize: 8,
                marginTop: 1
            },
            legend: {
                fontSize: 8
            },
            auto_result: {
                italics: true
            }
        }
    }

    pdfMake.createPdf( content ).download( 'ctl-' + event.value.ctlId + '-' + ( final ? 'results' : 'provisional-results' ) + '-'
        + eventClass.boatClass.shortcut.toLowerCase() + ( categoryDisplayed.value !== null ? '-' + ( categoryList.value.find( c => c.value === categoryDisplayed.value )?.filename ?? '' ) : '' )
        + '.pdf', () => {
        if ( event.value.forceEnglishReports )
            useRepo( SettingsRepo ).triggerActions( Options.Language )
    } )
}
</script>

<style scoped lang="sass">
.minicrewlist
  font-size: 80% !important

.auto_result
  font-style: italic

.captain
  font-weight: bold

.filter-bar
  font-size: 80%
</style>
